import { extensionCheck } from './checks/extension-check';
import { monitorCheck } from './checks/monitor-check';
import { screenShareCheck } from './checks/screen-share-check';
import { webcamCheck } from './checks/webcam-check';
import { GLOBAL_TIMEOUT_MS } from './constants';
import {
  CheckFn,
  CheckResult,
  EnvironmentCheckPolicy,
  EnvironmentCheckReport,
  EnvironmentCheckerOptions,
} from './types';

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Check timed out after ${ms}ms`)),
      ms,
    );
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      err => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

const BLOCKING_CHECK_IDS = new Set([
  'monitor_count',
  'webcam_virtual',
  'screen_share',
]);

function determineOverallStatus(
  results: CheckResult[],
): 'approved' | 'blocked' {
  const hasBlockingFailure = results.some(
    r => r.status === 'failed' && BLOCKING_CHECK_IDS.has(r.checkId),
  );

  return hasBlockingFailure ? 'blocked' : 'approved';
}

const ALL_CHECKS: CheckFn[] = [
  monitorCheck,
  webcamCheck,
  extensionCheck,
  screenShareCheck,
];

const CHECK_ID_TO_FN: Record<string, CheckFn> = {
  monitor_count: monitorCheck,
  webcam_virtual: webcamCheck,
  suspicious_extensions: extensionCheck,
  screen_share: screenShareCheck,
};

const POLICY_MAP: Record<string, keyof EnvironmentCheckPolicy> = {
  monitor_count: 'secondScreenEnabled',
  webcam_virtual: 'virtualWebcamEnabled',
  suspicious_extensions: 'suspiciousExtensionsEnabled',
  screen_share: 'screenShareEnabled',
};

function createSkippedResult(checkId: string, checkName: string): CheckResult {
  return {
    checkId,
    checkName,
    status: 'skipped',
    details: 'Verificação desativada para esta certificação.',
    timestamp: new Date().toISOString(),
    metadata: { reason: 'disabled_by_policy' },
  };
}

const CHECK_NAMES: Record<string, string> = {
  monitor_count: 'Segunda tela',
  webcam_virtual: 'Webcam virtual',
  suspicious_extensions: 'Extensões suspeitas',
  screen_share: 'Compartilhamento de tela',
};

const OPT_IN_CHECKS = new Set(['screen_share']);

function isCheckEnabled(
  checkId: string,
  policy?: EnvironmentCheckPolicy,
): boolean {
  if (!policy) return !OPT_IN_CHECKS.has(checkId);
  const policyKey = POLICY_MAP[checkId];
  if (!policyKey) return true;
  if (OPT_IN_CHECKS.has(checkId)) {
    return policy[policyKey] === true;
  }
  return policy[policyKey] !== false;
}

export async function runEnvironmentChecks(
  options: EnvironmentCheckerOptions,
  checksToRun: CheckFn[] = ALL_CHECKS,
): Promise<EnvironmentCheckReport> {
  const startedAt = new Date().toISOString();
  const startMs = Date.now();

  const timeoutMs = options.timeoutMs ?? GLOBAL_TIMEOUT_MS;
  const { policy } = options;

  const classified = checksToRun.map(checkFn => {
    const checkId = Object.keys(CHECK_ID_TO_FN).find(
      id => CHECK_ID_TO_FN[id] === checkFn,
    );
    return {
      checkFn,
      checkId,
      enabled: !checkId || isCheckEnabled(checkId, policy),
    };
  });

  const skippedResults: CheckResult[] = classified
    .filter(c => !c.enabled && c.checkId)
    .map(c =>
      createSkippedResult(c.checkId!, CHECK_NAMES[c.checkId!] ?? c.checkId!),
    );

  const executedResults = await Promise.all(
    classified
      .filter(c => c.enabled)
      .map(c => withTimeout(c.checkFn(), timeoutMs)),
  );

  const results = [...executedResults, ...skippedResults];

  const completedAt = new Date().toISOString();
  const durationMs = Date.now() - startMs;

  const overallStatus = determineOverallStatus(results);

  return {
    candidateId: options.candidateId,
    certificationLogId: options.certificationLogId,
    overallStatus,
    checks: results,
    startedAt,
    completedAt,
    durationMs,
    browserInfo: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
    },
  };
}

export async function retryFailedChecks(
  previousReport: EnvironmentCheckReport,
  options: EnvironmentCheckerOptions,
): Promise<EnvironmentCheckReport> {
  const { policy } = options;

  const checksToRetry = previousReport.checks
    .filter(check => {
      if (check.status === 'skipped') return false;
      const fn = CHECK_ID_TO_FN[check.checkId];
      if (!fn) return false;
      return isCheckEnabled(check.checkId, policy);
    })
    .map(check => CHECK_ID_TO_FN[check.checkId]);

  if (checksToRetry.length === 0) {
    return previousReport;
  }

  const startMs = Date.now();
  const timeoutMs = options.timeoutMs ?? GLOBAL_TIMEOUT_MS;

  const retryResults = await Promise.all(
    checksToRetry.map(check => withTimeout(check(), timeoutMs)),
  );

  const retryResultMap = new Map(retryResults.map(r => [r.checkId, r]));
  const mergedChecks: CheckResult[] = previousReport.checks.map(prev => {
    return retryResultMap.get(prev.checkId) ?? prev;
  });

  const completedAt = new Date().toISOString();
  const durationMs = Date.now() - startMs;

  return {
    ...previousReport,
    checks: mergedChecks,
    overallStatus: determineOverallStatus(mergedChecks),
    completedAt,
    durationMs: previousReport.durationMs + durationMs,
  };
}

export { ALL_CHECKS };
export type {
  EnvironmentCheckPolicy,
  EnvironmentCheckReport,
  EnvironmentCheckerOptions,
  CheckResult,
};
